/* Состояние «Своего референса» (BotRef, APP-SPEC-5 §1.10): слоты-загрузки формы с назначением reference. */
import { useEffect, useId, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import type { useStudioForm } from '../../studio/use-studio-form';
import type { FormField } from './form-model';

type FormState = ReturnType<typeof useStudioForm>;
export const REFERENCE_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

async function shortSide(file: File) {
  try {
    if (typeof createImageBitmap !== 'function') return null;
    const bitmap = await createImageBitmap(file);
    const side = Math.min(bitmap.width, bitmap.height);
    bitmap.close?.();
    return side;
  } catch {
    return null;
  }
}

export function useReference(state: FormState, slots: FormField[], onError: (message: string) => void) {
  const [local, setLocal] = useState<Record<string, { url: string; name: string; size: number }>>({});
  const [checking, setChecking] = useState(false);
  const inputId = `shk-ref-${useId().replace(/:/g, '')}`;
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach(url => URL.revokeObjectURL(url)), []);
  const multi = slots.length > 1;
  const stored = slots.filter(slot => state.uploadIds[slot.key] || state.inputs[slot.key]);
  const on = slots.some(slot => !!state.inputs[slot.key]);
  const select = (value: boolean) =>
    state.setInputs(current => {
      const next = { ...current };
      for (const slot of slots) {
        if (value && state.uploadIds[slot.key]) next[slot.key] = state.uploadIds[slot.key];
        else if (!value) delete next[slot.key];
      }
      return next;
    });
  const clear = () => {
    slots.forEach(slot => state.clearUpload(slot.key));
    setLocal({});
  };
  /** Возвращает, встала ли хоть одна картинка. */
  async function add(files: FileList | null): Promise<boolean> {
    if (!files?.length) return false;
    if (!state.rights) {
      onError(uiText('Сначала отметьте согласие ниже'));
      return false;
    }
    const free = slots.filter(slot => !state.uploadIds[slot.key] && !state.inputs[slot.key]);
    const targets = multi ? free : slots;
    const chosen = Array.from(files).slice(0, targets.length);
    if (!chosen.length) {
      onError(uiFormat('Можно до {0} фото', slots.length));
      return false;
    }
    setChecking(true);
    let failed = 0;
    for (const [index, file] of chosen.entries()) {
      const side = await shortSide(file);
      const reason = !REFERENCE_ACCEPT.includes(file.type)
        ? 'Картинка не подошла: не открывается, попробуйте JPG или PNG'
        : file.size > 10 * 1024 * 1024 || (side !== null && side < 256)
          ? 'Картинка не подошла: слишком маленькая, нужна от 256 px'
          : '';
      if (!reason && (await state.upload(targets[index].key, file))) {
        const url = URL.createObjectURL(file);
        urls.current.push(url);
        setLocal(current => ({ ...current, [targets[index].key]: { url, name: file.name, size: file.size } }));
      } else {
        failed++;
        if (!multi) onError(uiText(reason || 'Картинка не подошла: на ней защищённое или запрещённое содержимое'));
      }
    }
    setChecking(false);
    if (multi && failed) onError(failed === 1 ? uiText('Фото не подошло') : uiFormat('{0} фото не подошли', failed));
    return failed < chosen.length;
  }
  return { slots, multi, stored, on, local, checking, inputId, select, clear, add, open: () => (document.getElementById(inputId) as HTMLInputElement | null)?.click() };
}
export type ReferenceModel = ReturnType<typeof useReference>;

